import { connect } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Route, useLocation } from 'react-router-dom';
import React from 'react';
import createCachedSelector from 're-reselect';

import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import GridLoading from '../../../../components/grid-loading';
import { OrdersGridContainerShipmentMode } from '../orders-grid-container/orders-grid-container.shipment-mode';
import { OrdersGridPagingControl } from '../orders-grid-paging-control';
import { OrdersGridDrawerContainer } from '../../../../containers/orders-drawer';
import ErrorBoundary from '../../../../components/error-boundary';

import { resolveRoute } from '../../../../services/shipment-details-route-resolver';
import SellerPresetsHotkey from '../../../../components/hotkeys/seller-presets';
import { ShipmentModalLoader } from '../../../../components/modals/shipment-details-modal/shipment-modal-loader';
import { FulfillmentPlanService } from '../../../../services/fulfillment-plan-service';

import style from '../../../grid-page.module.less';

const Sidebar = () => (
  <div className={style['side-bar']}>
    <ErrorBoundary errorMessageId='drawer'>
      <OrdersGridDrawerContainer />
    </ErrorBoundary>
  </div>
);

// This looks kind of gnarly and useless, but basically, from one state update
// to another, we don't want different references for [123] and [123], so we
// stringify the incoming array of keys, to use as a cache key, and if we need
// to process this, we then have an input selector function that receives the
// incoming fpIds and just returns it as a parameter to to the final function,
// which also just receives it and returns it... Any subsequent calls to this
// function with the same set of id's will receive back the same array ref due
// to the cache key matching. For empty sets, we just return an emptyArray.
const selectHotkeyedFulfillmentPlanIds = createCachedSelector(
  (fulfillmentPlanIds) => fulfillmentPlanIds,
  (fulfillmentPlanIds) => {
    if (fulfillmentPlanIds.length === 0) {
      return emptyArray;
    }

    return fulfillmentPlanIds;
  },
)((fulfillmentPlanIds) => JSON.stringify(fulfillmentPlanIds));

const mapStateToProps = (state) => {
  const fulfillmentPlanIds = Global.selectSelectedOrdersIds(state);
  const hotkeyedFulfillmentPlanIds = selectHotkeyedFulfillmentPlanIds(
    Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds })
      .filter(FulfillmentPlanService.shouldShowShipmentWidget)
      .map(({ fulfillmentPlanId }) => fulfillmentPlanId),
  );

  return {
    isLoading: Global.selectOrdersGridIsLoading(state),
    isGroupBysCountLoading: Global.selectOrdersGridIsGroupBysCountLoading(state),
    isSidebarVisible: Global.selectOrdersGridisSidebarVisible(state),
    hotkeyedFulfillmentPlanIds,
  };
};

const OrdersGridPresetsContainer = ({
  isLoading = false,
  isGroupBysCountLoading = false,
  isSidebarVisible = true,
  hotkeyedFulfillmentPlanIds,
}) => {
  const isGridLoading = isLoading || isGroupBysCountLoading;
  const { pathname } = useLocation();

  return (
    <>
      <SellerPresetsHotkey fulfillmentPlanIds={hotkeyedFulfillmentPlanIds}>
        <div className={style['grid-content']}>
          <div
            className={classnames(style['grid-and-footer'], {
              [style['no-sidebar']]: !isSidebarVisible,
            })}
          >
            <div className={style.grid}>
              {isGridLoading && <GridLoading />}
              <ErrorBoundary errorMessageId='ordersGrid'>
                <OrdersGridContainerShipmentMode isLoading={isGridLoading} />
              </ErrorBoundary>
            </div>
            <div className={[style['paging-footer']]}>
              <OrdersGridPagingControl />
            </div>
          </div>
          {isSidebarVisible && <Sidebar />}
        </div>
      </SellerPresetsHotkey>
      <Route component={ShipmentModalLoader} path={resolveRoute(pathname)} />
    </>
  );
};

OrdersGridPresetsContainer.propTypes = {
  isLoading: PropTypes.bool,
  isGroupBysCountLoading: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  hotkeyedFulfillmentPlanIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
};

export default connect(mapStateToProps)(OrdersGridPresetsContainer);
